import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { emailTemplateService } from '../../../services/emailTemplateService';

const EmailTemplateEditor = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [formData, setFormData] = useState({
    template_name: '',
    subject_template: '',
    html_template: ''
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await emailTemplateService?.getAllTemplates();
      setTemplates(data || []);
      if (data?.length > 0) {
        setSelectedTemplate(data?.[0]);
        setFormData({
          template_name: data?.[0]?.template_name,
          subject_template: data?.[0]?.subject_template,
          html_template: data?.[0]?.html_template
        });
      }
    } catch (err) {
      setError(err?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setFormData({
      template_name: template?.template_name,
      subject_template: template?.subject_template,
      html_template: template?.html_template
    });
    setEditMode(false);
    setPreviewMode(false);
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await emailTemplateService?.updateTemplate(selectedTemplate?.id, formData);
      setSuccess('Template saved successfully!');
      setEditMode(false);
      await loadTemplates();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      template_name: selectedTemplate?.template_name,
      subject_template: selectedTemplate?.subject_template,
      html_template: selectedTemplate?.html_template
    });
    setEditMode(false);
    setError(null);
  };

  const insertField = (field) => {
    const placeholder = `{{${field}}}`;
    setFormData(prev => ({
      ...prev,
      html_template: prev?.html_template + placeholder
    }));
  };

  const getPreviewHtml = () => {
    if (!selectedTemplate) return '';
    const sampleData = emailTemplateService?.getSampleData(selectedTemplate?.form_type);
    const rendered = emailTemplateService?.renderTemplate(
      { ...selectedTemplate, ...formData },
      sampleData
    );
    return rendered?.html || '';
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      setError('Please enter a test email address');
      return;
    }

    try {
      setSendingTest(true);
      setError(null);
      const sampleData = emailTemplateService?.getSampleData(selectedTemplate?.form_type);
      await emailTemplateService?.sendTestEmail(selectedTemplate?.id, sampleData, testEmail);
      setSuccess(`Test email sent to ${testEmail}!`);
      setTestEmail('');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const getFormTypeIcon = (formType) => {
    const icons = {
      contact: 'Mail',
      consultation: 'Calendar',
      partnership: 'Handshake'
    };
    return icons?.[formType] || 'Mail';
  };

  const getFormTypeColor = (formType) => {
    const colors = {
      contact: 'bg-blue-500',
      consultation: 'bg-green-500',
      partnership: 'bg-purple-500'
    };
    return colors?.[formType] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-foreground">Email Templates</h2>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            Customize notification email templates for different form types
          </p>
        </div>
      </div>
      {/* Alerts */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start gap-3"
        >
          <Icon name="AlertCircle" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-red-500 text-sm">{error}</p>
        </motion.div>
      )}
      {success && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 flex items-start gap-3"
        >
          <Icon name="CheckCircle" size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
          <p className="text-green-500 text-sm">{success}</p>
        </motion.div>
      )}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Template List */}
        <div className="xl:col-span-1 space-y-3">
          <h3 className="text-base sm:text-lg font-semibold text-foreground mb-4">Form Types</h3>
          {templates?.map((template) => (
            <motion.button
              key={template?.id}
              onClick={() => handleTemplateSelect(template)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full p-4 rounded-lg border-2 transition-all text-left touch-manipulation ${
                selectedTemplate?.id === template?.id
                  ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-primary/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${getFormTypeColor(template?.form_type)}`}>
                  <Icon name={getFormTypeIcon(template?.form_type)} size={20} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-foreground capitalize text-sm sm:text-base truncate">
                    {template?.form_type}
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground truncate">
                    {template?.template_name}
                  </p>
                </div>
                {selectedTemplate?.id === template?.id && (
                  <Icon name="Check" size={20} className="text-primary flex-shrink-0" />
                )}
              </div>
            </motion.button>
          ))}
        </div>

        {/* Template Editor */}
        <div className="xl:col-span-2 space-y-6">
          {selectedTemplate && (
            <>
              {/* Mode Toggle */}
              <div className="flex gap-2">
                <button
                  onClick={() => { setEditMode(true); setPreviewMode(false); }}
                  className={`flex-1 py-2 px-3 sm:px-4 rounded-lg font-medium transition-all text-sm sm:text-base touch-manipulation ${
                    editMode && !previewMode
                      ? 'bg-primary text-white' :'bg-card text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name="Edit" size={18} className="inline mr-2" />
                  Edit
                </button>
                <button
                  onClick={() => { setPreviewMode(true); setEditMode(false); }}
                  className={`flex-1 py-2 px-3 sm:px-4 rounded-lg font-medium transition-all text-sm sm:text-base touch-manipulation ${
                    previewMode
                      ? 'bg-primary text-white' :'bg-card text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name="Eye" size={18} className="inline mr-2" />
                  Preview
                </button>
              </div>

              {/* Available Fields */}
              {editMode && !previewMode && (
                <div className="bg-card border border-border rounded-lg p-4">
                  <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2 text-sm sm:text-base">
                    <Icon name="Code" size={18} />
                    Available Fields
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedTemplate?.available_fields?.map((field, index) => (
                      <button
                        key={index}
                        onClick={() => insertField(field?.field)}
                        className="px-3 py-1.5 bg-muted hover:bg-primary hover:text-white rounded-md text-sm font-mono transition-all"
                        title={field?.description}
                      >
                        {`{{${field?.field}}}`}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    Click a field to insert it into your template
                  </p>
                </div>
              )}

              {/* Editor */}
              {editMode && !previewMode && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Template Name
                    </label>
                    <input
                      type="text"
                      value={formData?.template_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, template_name: e?.target?.value }))}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Subject Template
                    </label>
                    <input
                      type="text"
                      value={formData?.subject_template}
                      onChange={(e) => setFormData(prev => ({ ...prev, subject_template: e?.target?.value }))}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                      placeholder="Use {{field}} for dynamic content"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      HTML Template
                    </label>
                    <textarea
                      value={formData?.html_template}
                      onChange={(e) => setFormData(prev => ({ ...prev, html_template: e?.target?.value }))}
                      rows={15}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground font-mono text-sm"
                      placeholder="Use {{field}} for dynamic content"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex-1 bg-primary text-white py-2 px-4 rounded-lg font-medium hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={handleCancel}
                      className="px-4 py-2 border border-border rounded-lg font-medium hover:bg-muted transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Preview */}
              {previewMode && (
                <div className="space-y-4">
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Icon name="Mail" size={18} />
                      Email Preview
                    </h4>
                    <div className="bg-background border border-border rounded-lg p-4 mb-4">
                      <p className="text-sm text-muted-foreground mb-1">Subject:</p>
                      <p className="font-medium text-foreground">
                        {emailTemplateService?.renderTemplate(
                          { ...selectedTemplate, ...formData },
                          emailTemplateService?.getSampleData(selectedTemplate?.form_type)
                        )?.subject}
                      </p>
                    </div>
                    <div
                      className="border border-border rounded-lg overflow-auto max-h-[500px]"
                      dangerouslySetInnerHTML={{ __html: getPreviewHtml() }}
                    />
                  </div>

                  {/* Test Email */}
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Icon name="Send" size={18} />
                      Send Test Email
                    </h4>
                    <div className="flex gap-3">
                      <input
                        type="email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e?.target?.value)}
                        placeholder="Enter test email address"
                        className="flex-1 px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                      />
                      <button
                        onClick={handleSendTest}
                        disabled={sendingTest || !testEmail}
                        className="bg-primary text-white py-2 px-6 rounded-lg font-medium hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {sendingTest ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Sending...
                          </>
                        ) : (
                          <>
                            <Icon name="Send" size={18} />
                            Send
                          </>
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Test email will be sent with sample data
                    </p>
                  </div>
                </div>
              )}

              {/* Read-only View */}
              {!editMode && !previewMode && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-foreground">
                      {selectedTemplate?.template_name}
                    </h3>
                    <button
                      onClick={() => setEditMode(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all"
                    >
                      <Icon name="Edit" size={18} />
                      Edit Template
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Subject:</p>
                      <p className="text-foreground font-mono text-sm">{selectedTemplate?.subject_template}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Available Fields:</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {selectedTemplate?.available_fields?.map((field, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-muted rounded text-xs font-mono"
                            title={field?.description}
                          >
                            {`{{${field?.field}}}`}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailTemplateEditor;